import { API } from "@infra/api";
import { logger } from "@infra/logger";
import type { IJobCreation } from "@ports/jobs/IJobCreation";
import type { IJobs } from "@ports/jobs/IJobs";
import type { IJobIssues } from "@ports/jobs/IJobIssues";
import type { Branch } from "@domain/CRM/branch";
import type { Contact } from "@domain/CRM/contact";
import type { Deal } from "@domain/CRM/deal";
import type { User } from "@domain/Auth";
import type { Grower } from "@domain/CRM/grower";
import type { Region } from "@domain/CRM/region";
import type { Job } from "@domain/CRM/job";
import {
	makeAgrianIssuesUseCases,
	makeDataSendbackIssuesUseCases,
	makeJobsDetailUseCases,
	makeJobsListUseCases,
	makeMarkJobsReadyUseCases,
	makeSubmitJobsUseCases,
} from "@app/jobs";

const jobCreationApi: IJobCreation = {
	getGrowersByFilter: (filter) => API.Data.Growers.get.byFilter(filter) as Promise<Grower[]>,
	createGrowers: (growers) => API.Data.Growers.create.many(growers) as Promise<Grower[]>,
	createContacts: (contacts) => API.Data.Contacts.create.many(contacts) as Promise<Contact[]>,
	getContactsByIds: (ids) => API.Data.Contacts.get.byIds(ids) as Promise<Contact[]>,
	getBranchesByIds: (ids) => API.Data.Branches.get.byIds(ids) as Promise<Branch[]>,
	updateContacts: (contacts) => API.Data.Contacts.update.many(contacts) as unknown as Promise<void>,
	updateBranches: (branches) => API.Data.Branches.update.many(branches) as unknown as Promise<void>,
};

const jobsApi: IJobs = {
	getById: (id, useCache) => API.Data.Jobs.get.byId(id, useCache) as Promise<Job | undefined>,
	getByCompany: (company, branchNames) => API.Data.Jobs.get.byCompany(company, branchNames) as Promise<Job[]>,
	getByCompanyStream: (company, branchNames, onBatch) =>
		API.Data.Jobs.get.byCompanyStream(company, branchNames, onBatch) as Promise<Job[]>,
	getUnscheduled: () => API.Data.Jobs.get.unscheduled(),
	getAttachmentsByIds: (ids) => API.Data.Jobs.get.attachmentsByIds(ids),
	getLastCropOptions: () => API.Data.Jobs.get.lastCropOptions(),
	getNextCropOptions: () => API.Data.Jobs.get.nextCropOptions(),
	updateOne: (job) => API.Data.Jobs.update.one(job as Job) as Promise<Job>,
	updateMany: (jobs, compressed) => API.Data.Jobs.update.many(jobs, compressed) as unknown as Promise<void>,
	updateField: (id, field, value) => API.Data.Jobs.update.field(id, field, value) as Promise<Job>,
};

const jobIssuesApi: IJobIssues = {
	getAgrian: () => API.Data.Jobs.issues.getAgrian(),
	fixAgrian: (ids) => API.Data.Jobs.issues.fixAgrian(ids),
	getDataSendback: () => API.Data.Jobs.issues.getDataSendback(),
	fixDataSendback: (ids, forceEmail, forceApi) => API.Data.Jobs.issues.fixDataSendback(ids, forceEmail, forceApi),
	getBranchMatcherJobs: () => API.Data.Jobs.issues.getBranchMatcherJobs(),
	getDealMatcherJobs: () => API.Data.Jobs.issues.getDealMatcherJobs(),
	fixDealMatcherJobs: (ids) => API.Data.Jobs.issues.fixDealMatcherJobs(ids),
	fixExeMissionValidationJobs: (ids) => API.Data.Jobs.issues.fixExeMissionValidationJobs(ids),
};

/**
 * Bound jobs use cases. Each sub-namespace is the output of one narrow
 * factory; consumers go through the single `useJobUseCases()` hook and
 * descend into the relevant sub-namespace (`jobs.list.loadByFilter`,
 * `jobs.markReady.loadGrowersWithUnreadyJobs`, etc.).
 *
 * The aggregation lives at the composition layer, not the Application layer,
 * because the deps required for each sub-feature are different and only the
 * composition root knows how to wire them. The Application factories stay
 * narrow so unit tests can construct one sub-namespace at a time.
 */
export const jobUseCases = {
	list: makeJobsListUseCases({ jobsApi }),
	detail: makeJobsDetailUseCases({ jobsApi }),
	agrianIssues: makeAgrianIssuesUseCases({ jobIssuesApi }),
	dataSendback: makeDataSendbackIssuesUseCases({ jobIssuesApi, jobsApi }),
	markReady: makeMarkJobsReadyUseCases({
		createJobsReady: (form) => API.Data.JobsReady.create.one(form) as Promise<any>,
		updateJobs: (jobs, compressed) => API.Data.Jobs.update.many(jobs, compressed) as unknown as Promise<void>,
		getGrowersWithUnreadyJobs: (company) => API.Data.Growers.get.withUnreadyJobs(company) as Promise<Grower[]>,
		getJobsForGrowers: (growers) => API.Data.Jobs.get.byGrowers(growers) as Promise<Job[]>,
		logger,
	}),
	submit: makeSubmitJobsUseCases({
		jobCreationApi,
		getCurrentUser: () => API.Users.me() as Promise<User>,
		getDealsByIds: (ids) => API.Data.Deals.get.byIds(ids) as Promise<Deal[]>,
		createJobs: (jobs) => API.Data.Jobs.create.many(jobs) as Promise<Job[]>,
		logger,
		getBranchesByCompany: (company) => API.Data.Branches.get.byCompany(company) as Promise<Branch[]>,
		getRegionsByCompany: (company) => API.Data.Regions.get.byCompany(company) as unknown as Promise<Region[]>,
		getGrowersByCompany: (company) => API.Data.Growers.get.byCompany(company) as Promise<Grower[]>,
		getDealsByCompany: (company) => API.Data.Deals.get.byCompany(company) as Promise<Deal[]>,
		getContactsByCompany: (company) => API.Data.Contacts.get.byCompany(company) as Promise<Contact[]>,
		getLastCropOptions: () => API.Data.Jobs.get.lastCropOptions(),
		getNextCropOptions: () => API.Data.Jobs.get.nextCropOptions(),
		updateGrowerPhoneNumber: (growerId, phoneNumber) => API.Data.Growers.update.phoneNumber(growerId, phoneNumber),
	}),
};

export type JobUseCases = typeof jobUseCases;
